// Recent Form / Home-Away Form — W/D/L badge strips (Phase 3 → M2 wave 5).
//
// The first React+D3 coexistence widget: React owns the container (the per-team
// strip shells), D3 owns the badges inside each `.form-container` via a keyed
// enter/update/exit join in a useEffect — a direct port of the legacy
// renderFormModule's `renderStrip`, so the badges animate (opacity) on control
// change instead of snapping (the animation rule). Emits the same DOM + classes
// as the legacy renderer (verified by the VRT baseline).
//
// React never renders children into `.form-container` (D3 fills it), and the
// `.form-team` elements are keyed by team, so on a fixture change React swaps the
// shells (fresh containers → badges enter from 0) and on a timeline/venue change
// it keeps them (D3 updates in place → animated). Pure decisions (result, colour,
// tooltip, venue, window) live in `@bento/shared`.
//
// Data now comes from app-api via `useWidgetData` (was the `getFormData(venue)`
// legacyData getter). This component serves BOTH titles, so it fetches a
// variant-keyed endpoint returning DIFFERENT payloads (TopScorers/Assists
// precedent): Recent Form → `recent-form` (a `FormData`); Home/Away Form →
// `home-away-form` (an envelope `{home, away}` — the ha toggle picks the slice
// client-side, no refetch, since both venues ride in the one payload). TS can't
// correlate the runtime key with `isHomeAway`, so each branch casts `data`.

import { useEffect, useRef } from "react";
import { select } from "d3-selection";
import "d3-transition";
import { matchResult, formResultColor, formTooltip, formVenue, formWindow, selectForm, type FormData, type FormMatch, type HomeAwayFormPayload } from "@bento/shared";
import { useWidgetData } from "../../data/useWidgetData.js";
import { useFixtureKey } from "../../data/useFixtureKey.js";
import WidgetSkeleton from "./WidgetSkeleton.js";
import WidgetError from "./WidgetError.js";

const DURATION = 300;

export interface FormBadgesProps {
  /** Home/Away Form (true) vs Recent Form (false). */
  isHomeAway: boolean;
  fixture: string;
  timeline?: string | null;
  ha?: string | null;
}

/** D3 keyed join for one team's strip — the legacy renderStrip, verbatim except
 *  the FormData is now passed in (fetched) rather than read from the getter. */
function renderStrip(
  container: HTMLElement,
  team: string,
  formData: FormData,
  n: number
): void {
  const matches = selectForm(formData[team] ?? [], n);

  const boxes = select<HTMLElement, unknown>(container)
    .selectAll<HTMLDivElement, FormMatch>(".form-box")
    .data(matches, (m) => `${m.date}${m.home}${m.away}`);

  const entering = boxes.enter().append("div").style("opacity", 0);

  // Reorder DOM to data order (an entering match may belong left of existing ones).
  entering
    .merge(boxes)
    .order()
    .each(function (this: HTMLDivElement, m: FormMatch) {
      const r = matchResult(m, team);
      this.className = `form-box form-${r.toLowerCase()} bar-lit`;
      this.innerHTML = `<div class="form-box-bg bento-fill" style="background:${formResultColor(r)}"></div>`;
      this.setAttribute("data-tooltip", formTooltip(m));
    })
    .transition()
    .duration(DURATION)
    .style("opacity", null);

  boxes.exit().transition().duration(DURATION).style("opacity", 0).remove();
}

export default function FormBadges({
  isHomeAway,
  fixture,
  timeline,
  ha,
}: FormBadgesProps) {
  const parts = (fixture || "Arsenal v Everton").split(" v ");
  const t1 = (parts[0] ?? "").trim();
  const t2 = (parts[1] ?? "").trim();
  const teams = t2 ? [t1, t2] : [t1];

  const venue = formVenue(isHomeAway, ha);
  const n = formWindow(timeline);

  const key = isHomeAway ? "home-away-form" : "recent-form";
  const { data, isPending, isError, refetch } = useWidgetData(
    key,
    useFixtureKey(fixture)
  );

  const containers = useRef<Record<string, HTMLDivElement | null>>({});

  // Hooks run unconditionally (above the isPending/isError early returns). `data`
  // is in the deps so the strip draws once the fetch resolves — React Query's
  // `data` ref is stable, so this re-runs exactly on data-arrival + control
  // changes (not every render). While pending the wrapper isn't mounted, so
  // `containers.current` is empty and the loop is a no-op.
  useEffect(() => {
    // Resolve the venue's FormData from the fetched payload. Recent Form → the
    // payload IS the FormData; Home/Away Form → pick home/away from the envelope.
    const formData: FormData = isHomeAway
      ? ((venue === "away"
          ? (data as HomeAwayFormPayload | undefined)?.away
          : (data as HomeAwayFormPayload | undefined)?.home) ?? {})
      : ((data as FormData | undefined) ?? {});
    for (const team of teams) {
      const el = containers.current[team];
      if (el) renderStrip(el, team, formData, n);
    }
    // Deps: fixture (drives `teams`), venue (ha), n (timeline), isHomeAway, data.
    // `renderStrip` is module-scope and `containers` is a ref — both stable.
  }, [fixture, venue, n, isHomeAway, data]);

  if (isPending) return <WidgetSkeleton />;
  if (isError) return <WidgetError onRetry={refetch} />;

  return (
    <div className="data-wrapper top-align">
      {teams.map((team) => (
        <div className="form-team" data-team={team} key={team}>
          <div className="form-team-label">{team}</div>
          <div
            className="form-container"
            ref={(el) => {
              containers.current[team] = el;
            }}
          />
        </div>
      ))}
    </div>
  );
}
